import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { FaAward, FaHandshake, FaTools, FaUsers } from 'react-icons/fa';

const About = () => {
  const values = [
    {
      icon: <FaHandshake />,
      title: "Trust & Integrity",
      description: "Building lasting relationships through honest, transparent service.",
      color: "#FF6B00"
    },
    {
      icon: <FaAward />,
      title: "Quality Excellence",
      description: "Delivering superior craftsmanship with premium materials.",
      color: "#FF7A00"
    },
    {
      icon: <FaTools />,
      title: "Expert Solutions",
      description: "Innovative approaches backed by decades of experience.",
      color: "#FF8900"
    },
    {
      icon: <FaUsers />,
      title: "Customer Focus",
      description: "Your satisfaction drives every decision we make.",
      color: "#FF9800"
    }
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  return (
    <>
      <Helmet>
        <title>About Us | Xtreme Roofing & Construction</title>
        <meta name="description" content="Learn about our commitment to quality and customer satisfaction in roofing and construction." />
      </Helmet>

      <PageContainer>
        <HeroSection>
          <HeroContent>
            <Title
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Our Story
            </Title>
            <Subtitle
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Building Excellence Since 2000
            </Subtitle>
          </HeroContent>
          <HeroGradient />
          <ScrollIndicator 
            animate={{ 
              y: [0, 10, 0],
              opacity: [0.8, 0.4, 0.8]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            ↓
          </ScrollIndicator>
        </HeroSection>

        <ContentSection>
          <StorySection {...fadeInUp}>
            <SectionTitle>Who We Are</SectionTitle>
            <StoryText>
              For over two decades, Xtreme Roofing & Construction has been setting the standard for excellence in the roofing and construction industry. What started as a small family business has grown into a trusted leader in residential and commercial construction, while maintaining our core values of quality, integrity, and customer satisfaction.
            </StoryText>
          </StorySection>

          <ValuesGrid>
            {values.map((value, index) => (
              <ValueCard
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10, scale: 1.02 }}
              >
                <IconWrapper $color={value.color}>
                  <IconBackground />
                  {value.icon}
                </IconWrapper>
                <ValueTitle>{value.title}</ValueTitle>
                <ValueDescription>{value.description}</ValueDescription>
              </ValueCard>
            ))}
          </ValuesGrid>

          <StatsSection>
            {[
              { number: "20+", label: "Years Experience" },
              { number: "15k+", label: "Projects Completed" },
              { number: "98%", label: "Client Satisfaction" }
            ].map((stat, index) => (
              <StatCard
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 + index * 0.1 }}
                whileHover={{ y: -5, scale: 1.05 }}
              >
                <StatNumber>{stat.number}</StatNumber>
                <StatLabel>{stat.label}</StatLabel>
                <StatGlow />
              </StatCard>
            ))}
          </StatsSection>
        </ContentSection>
      </PageContainer>
    </>
  );
};

const PageContainer = styled.div`
  min-height: 100vh;
  background: var(--background-dark);
  color: white;
`;

const HeroSection = styled.section`
  min-height: 50vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8rem 2rem 4rem;
  position: relative;
  overflow: hidden;
`;

const HeroGradient = styled.div`
  position: absolute;
  inset: 0;
  background: 
    radial-gradient(70% 50% at 50% 0%, rgba(255, 107, 0, 0.15) 0%, transparent 100%),
    radial-gradient(50% 80% at 20% 100%, rgba(255, 107, 0, 0.1) 0%, transparent 100%);
  pointer-events: none;
`;

const HeroContent = styled.div`
  text-align: center;
  max-width: 800px;
  position: relative;
  z-index: 1;
`;

const Title = styled(motion.h1)`
  font-size: clamp(3rem, 6vw, 4.5rem);
  font-weight: 900;
  background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 1.5rem;
  letter-spacing: -0.02em;
`;

const Subtitle = styled(motion.p)`
  font-size: clamp(1.2rem, 2vw, 1.4rem);
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
`;

const ContentSection = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 6rem 2rem;
`;

const StorySection = styled(motion.div)`
  text-align: center;
  margin-bottom: 6rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  color: var(--primary);
  margin-bottom: 2rem;
  font-weight: 700;
`;

const StoryText = styled.p`
  font-size: 1.2rem;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.8);
  max-width: 800px;
  margin: 0 auto;
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin: 4rem 0;
`;

const ValueCard = styled(motion.div)`
  background: rgba(44, 44, 44, 0.95);
  padding: 3rem 2rem;
  border-radius: 20px;
  text-align: center;
  border: 1px solid rgba(255, 107, 0, 0.1);
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      135deg,
      transparent 0%,
      rgba(255, 107, 0, 0.05) 50%,
      transparent 100%
    );
    opacity: 0;
    transition: opacity 0.4s ease;
  }

  &:hover::before {
    opacity: 1;
  }
`;

const IconWrapper = styled.div`
  font-size: 2.5rem;
  color: ${props => props.$color || 'var(--primary)'};
  width: 80px;
  height: 80px;
  background: rgba(255, 107, 0, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  position: relative;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
`;

const IconBackground = styled.div`
  position: absolute;
  inset: -15%;
  background: radial-gradient(
    circle at center,
    ${props => props.$color || 'var(--primary)'} 0%,
    transparent 70%
  );
  opacity: 0;
  transition: opacity 0.3s ease;

  ${IconWrapper}:hover & {
    opacity: 0.15;
  }
`;

const ValueTitle = styled.h3`
  font-size: 1.5rem;
  color: white;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const ValueDescription = styled.p`
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
`;

const StatsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin-top: 6rem;
  text-align: center;
`;

const StatCard = styled(motion.div)`
  padding: 2.5rem;
  background: rgba(255, 107, 0, 0.1);
  border-radius: 20px;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 107, 0, 0.1);
`;

const StatNumber = styled.div`
  font-size: 3rem;
  font-weight: 800;
  color: var(--primary);
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
`;

const StatGlow = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(
    circle at center,
    var(--primary) 0%,
    transparent 70%
  );
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: inherit;

  ${StatCard}:hover & {
    opacity: 0.1;
  }
`;

const ScrollIndicator = styled(motion.div)`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  font-size: 2rem;
  color: var(--primary);
`;

export default About; 